import { API_BASE_URL } from '../lib/constants';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Edit, 
  Trash2, 
  Plus, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  FileText, 
  Phone, 
  Mail, 
  User, 
  Stethoscope, 
  Activity,
  BarChart3,
  Grid3X3,
  MessageSquare
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as BarTooltip, Legend } from 'recharts';

const PatientListTable = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [viewGraph, setViewGraph] = useState(false);
  const rowsPerPage = 5;

  // Fetch feedback data on component mount
  useEffect(() => {
    const fetchFeedbacks = async () => {
      setLoading(true);
      try {
  const response = await axios.get(`${API_BASE_URL}/get_feedbacks`);
        setFeedbacks(response.data.feedbacks || []);
      } catch (error) {
        console.error('Error fetching feedbacks:', error);
        setFeedbacks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbacks();
  }, []);

  // Filter feedbacks based on search term
  const filteredFeedbacks = feedbacks.filter(feedback =>
    (feedback.patient_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (feedback.mobile_no || "").toString().includes(searchTerm) ||
    (feedback.uhid_no || "").toString().includes(searchTerm) ||
    (feedback.doctor_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Paginated feedbacks
  const totalPages = Math.ceil(filteredFeedbacks.length / rowsPerPage);
  const paginatedFeedbacks = filteredFeedbacks.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  // const handleEdit = (id) => {
  //   navigate(`/editFeedback/${id}`);
  // };

  const handleDelete = (feedback) => {
    setDeleteConfirm(feedback);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
  const response = await axios.delete(`${API_BASE_URL}/delete_feedback/${deleteConfirm.id}`);
      if (response.status === 200) {
        setFeedbacks(prev => prev.filter(fb => fb.id !== deleteConfirm.id));
        setDeleteConfirm(null);
      }
    } catch (error) {
      console.error("Error deleting feedback:", error);
    }
  };

  const handleToggleView = () => {
    setViewGraph(!viewGraph);
  };

  const resetSearch = () => {
    setSearchTerm('');
    setCurrentPage(0);
  };

  // Function to get feedback reasons and counts
  const getFeedbackReasons = () => {
    const reasons = {
      'High Cost': 0,
      'No Insurance Available': 0,
      'Lack of Support': 0,
      'Other': 0,
    };

    filteredFeedbacks.forEach((row) => {
      const feedback = row.feedback_reason;
      if (reasons[feedback] !== undefined) {
        reasons[feedback]++;
      } else {
        reasons['Other']++;
      }
    });

    return Object.keys(reasons).map((key) => ({ name: key, value: reasons[key] }));
  };

  // Function to calculate doctors attending negative feedback patients
  const getDoctorsAndPatientsCount = () => {
    const negativeFeedback = ['High Cost', 'No Insurance Available', 'Lack of Support'];
    const doctorAttendance = {};
    let negativeFeedbackCount = 0;

    filteredFeedbacks.forEach((row) => {
      if (negativeFeedback.includes(row.feedback_reason)) {
        negativeFeedbackCount++;
        doctorAttendance[row.doctor_name] = (doctorAttendance[row.doctor_name] || 0) + 1;
      }
    });

    return {
      negativeFeedbackCount,
      doctorAttendance: Object.keys(doctorAttendance).map(doctor => ({
        name: doctor,
        count: doctorAttendance[doctor],
      })),
    };
  };

  const { negativeFeedbackCount, doctorAttendance } = getDoctorsAndPatientsCount();

  if (loading) {
    return (
      <div className="w-full max-w-6xl mx-auto p-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-12 bg-gray-200 rounded mb-6"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* Header
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-2">
          <MessageSquare className="h-6 w-6 text-teal-600" />
          <h1 className="text-3xl font-bold text-gray-800">Patient Feedback Management</h1>
        </div>
        <p className="text-gray-600">Manage patient feedback and analyze satisfaction data efficiently</p>
      </div> */}

      {/* Action bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="relative w-full lg:w-2/3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search by patient name, mobile, UHID, or doctor name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
            />
            {searchTerm && (
              <button
                onClick={resetSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          
          <div className="flex gap-3 w-full lg:w-auto">
            <button
              onClick={() => window.location.href = '/addFeedback'}
              className="flex items-center gap-2 bg-teal-600 text-white px-4 py-3 rounded-lg hover:bg-teal-700 transition-colors font-medium flex-1 lg:flex-none justify-center"
            >
              <Plus className="h-4 w-4" />
              Add Feedback
            </button>
            <button
              onClick={handleToggleView}
              className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium flex-1 lg:flex-none justify-center border border-gray-300"
            >
              {viewGraph ? (
                <>
                  <Grid3X3 className="h-4 w-4" />
                  Table View
                </>
              ) : (
                <>
                  <BarChart3 className="h-4 w-4" />
                  Graph View
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Results summary */}
      {searchTerm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800">
            Found {filteredFeedbacks.length} feedback{filteredFeedbacks.length !== 1 ? 's' : ''} matching "{searchTerm}"
          </p>
        </div>
      )}

      {viewGraph ? (
        <div className="space-y-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-teal-900 text-center mb-4">Patient Feedback Distribution</h2>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getFeedbackReasons()}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                  >
                    {getFeedbackReasons().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#ff8042', '#0088FE', '#00C49F', '#FFBB28'][index % 4]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-teal-900 text-center mb-4">Doctors and Patients Feedback</h2>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Doctors Attending Negative Feedback', count: doctorAttendance.length },
                  { name: 'Total Negative Feedback Patients', count: negativeFeedbackCount },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Bar dataKey="count" fill="#8884d8" />
                  <BarTooltip />
                  <Legend />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-teal-50 border-b border-teal-100">
                    <th className="text-left p-4 font-semibold text-teal-900">S/No</th>
                    <th className="text-left p-4 font-semibold text-teal-900">Patient Name</th>
                    <th className="text-left p-4 font-semibold text-teal-900">Mobile No</th>
                    <th className="text-left p-4 font-semibold text-teal-900">UHID No</th>
                    <th className="text-left p-4 font-semibold text-teal-900">Feedback</th>
                    <th className="text-left p-4 font-semibold text-teal-900">Date</th>
                    <th className="text-left p-4 font-semibold text-teal-900">Doctor</th>
                    <th className="text-center p-4 font-semibold text-teal-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedFeedbacks.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-12 text-center">
                        <div className="flex flex-col items-center">
                          <Search className="h-16 w-16 text-gray-300 mb-4" />
                          <h3 className="text-lg font-medium text-gray-500 mb-2">No feedback found</h3>
                          <p className="text-gray-400 mb-4">Try adjusting your search criteria</p>
                          {searchTerm && (
                            <button
                              onClick={resetSearch}
                              className="text-teal-600 hover:text-teal-700 font-medium"
                            >
                              Clear search
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedFeedbacks.map((feedback, index) => (
                      <tr 
                        key={feedback.id} 
                        className={`border-b border-gray-100 hover:bg-teal-50/50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                        }`}
                      >
                        <td className="p-4">
                          <div className="font-medium text-gray-900">
                            {currentPage * rowsPerPage + index + 1}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="font-medium text-gray-900">{feedback.patient_name}</div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="h-4 w-4" />
                            <span>{feedback.mobile_no}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="text-gray-600">{feedback.uhid_no}</div>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            feedback.feedback_reason === 'High Cost' ? 'bg-red-100 text-red-800' :
                            feedback.feedback_reason === 'No Insurance Available' ? 'bg-orange-100 text-orange-800' :
                            feedback.feedback_reason === 'Lack of Support' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {feedback.feedback_reason}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="text-gray-600">{feedback.feedback_date}</div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Stethoscope className="h-4 w-4" />
                            <span className="truncate max-w-xs">{feedback.doctor_name}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-center gap-1">
                      
                            <button 
                              onClick={() => handleDelete(feedback)} 
                              className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
                              title="Delete Feedback"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="hidden sm:inline">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4">
            {paginatedFeedbacks.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <Search className="h-16 w-16 text-gray-300 mb-4 mx-auto" />
                <h3 className="text-lg font-medium text-gray-500 mb-2">No feedback found</h3>
                <p className="text-gray-400 mb-4">Try adjusting your search criteria</p>
                {searchTerm && (
                  <button
                    onClick={resetSearch}
                    className="text-teal-600 hover:text-teal-700 font-medium"
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              paginatedFeedbacks.map((feedback, index) => (
                <div key={feedback.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-teal-100 text-teal-800 px-2 py-1 rounded text-sm font-medium">
                          #{currentPage * rowsPerPage + index + 1}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          feedback.feedback_reason === 'High Cost' ? 'bg-red-100 text-red-800' :
                          feedback.feedback_reason === 'No Insurance Available' ? 'bg-orange-100 text-orange-800' :
                          feedback.feedback_reason === 'Lack of Support' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {feedback.feedback_reason}
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-lg mb-1">{feedback.patient_name}</h3>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">{feedback.mobile_no}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">UHID: {feedback.uhid_no}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Stethoscope className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600 truncate">Dr. {feedback.doctor_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">{feedback.feedback_date}</span>
                    </div>
                  </div>

                  {/* Action buttons for mobile */}
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    {/* <button 
                      onClick={() => handleEdit(feedback.id)} 
                      className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors text-sm font-medium" 
                      title="Edit Feedback"
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </button> */}
                    <button 
                      onClick={() => handleDelete(feedback)} 
                      className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors text-sm font-medium" 
                      title="Delete Feedback"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Pagination */}
      {filteredFeedbacks.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {currentPage * rowsPerPage + 1} to {Math.min((currentPage + 1) * rowsPerPage, filteredFeedbacks.length)} of {filteredFeedbacks.length} feedback entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(0)}
                disabled={currentPage === 0}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Start
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg">
                {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages - 1)}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                End
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Feedback</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete feedback from <strong>{deleteConfirm.patient_name}</strong>? 
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientListTable;